import { useEffect, useState } from "react";
import api from "@/lib/api";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

// Module-level cache so multiple pickers share one fetch.
let _cache = null;
let _inflight = null;

async function loadDestinations() {
  if (_cache) return _cache;
  if (_inflight) return _inflight;
  _inflight = Promise.all([
    api.get("/extensions").then((r) => r.data).catch(() => []),
    api.get("/ring-groups").then((r) => r.data).catch(() => []),
    api.get("/queues").then((r) => r.data).catch(() => []),
    api.get("/ivrs").then((r) => r.data).catch(() => []),
    api.get("/departments").then((r) => r.data).catch(() => []),
  ]).then(([ext, rg, q, ivr, dept]) => {
    _cache = { ext, rg, q, ivr, dept };
    _inflight = null;
    return _cache;
  });
  return _inflight;
}

export function invalidateDestinations() { _cache = null; }

const NO_TARGET = ["hangup", "repeat", ""];

export function useDestinations() {
  const [d, setD] = useState(_cache);
  useEffect(() => {
    let m = true;
    loadDestinations().then((x) => m && setD(x));
    return () => { m = false; };
  }, []);
  return d;
}

export function optionsForType(d, type) {
  if (!d) return [];
  const map = {
    extension: d.ext.map((e) => ({ value: e.id, label: `${e.extension} · ${e.display_name}` })),
    voicemail: d.ext.map((e) => ({ value: e.id, label: `${e.extension} · ${e.display_name} (voicemail)` })),
    ring_group: d.rg.map((x) => ({ value: x.id, label: `${x.extension} · ${x.name}` })),
    queue: d.q.map((x) => ({ value: x.id, label: `${x.extension} · ${x.name}` })),
    ivr: d.ivr.map((x) => ({ value: x.id, label: x.name })),
    department: d.dept.map((x) => ({ value: x.id, label: x.name })),
  };
  return map[type] || [];
}

export default function DestinationPicker({ type, value, onChange, testid }) {
  const d = useDestinations();
  if (NO_TARGET.includes(type)) {
    return (
      <div className="h-10 flex items-center px-2 text-xs text-muted-foreground" data-testid={testid}>
        No target required
      </div>
    );
  }
  const opts = optionsForType(d, type);
  return (
    <Select value={value ? String(value) : ""} onValueChange={onChange}>
      <SelectTrigger className="bg-secondary/40 border-border" data-testid={testid}>
        <SelectValue placeholder={d ? "Select target…" : "Loading…"} />
      </SelectTrigger>
      <SelectContent>
        {opts.map((o) => (
          <SelectItem key={String(o.value)} value={String(o.value)}>{o.label}</SelectItem>
        ))}
        {d && opts.length === 0 && (
          <div className="px-2 py-1.5 text-xs text-muted-foreground">No {type.replace("_", " ")} configured yet</div>
        )}
      </SelectContent>
    </Select>
  );
}
